import React, { SyntheticEvent } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { MatchStrength } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import StrengthIndicator from 'talent_matching/components/shared/strength_indicator';
import styled from 'styled-components';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import MatchingSkills from 'talent_matching/components/shared/matching_skills';
import { WarningTag } from '@grnhse/seedling/lib/birch/components/tags';

const t = withLocalePrefix('talent_matching');

type Props = {
  matchStrength: MatchStrength | null;
  matchScore: number | null;
  matchedSkills: Array<{ name: string; importance: number }>;
  overrideMatchStrength?: MatchStrength | null;
  resumeParsingStatus?: string;
  hasCalibrationSetting?: boolean;
  openCalibrationModal?: () => void;
};

const ButtonWrapper = styled.div`
  button {
    padding-left: 0;
  }
`;

const BaseMatchStrengthCell = ({
  matchStrength: rawMatchStrength,
  matchScore,
  matchedSkills,
  overrideMatchStrength,
  resumeParsingStatus,
  hasCalibrationSetting,
  openCalibrationModal,
}: Props) => {
  const hasScores = matchScore !== null && rawMatchStrength !== null;
  const parsingInProgress = resumeParsingStatus === 'in_progress';
  const parsingFailed = resumeParsingStatus === 'failed';

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    openCalibrationModal?.();
  };

  const isOverride = !!overrideMatchStrength;
  const matchStrength = isOverride ? overrideMatchStrength : rawMatchStrength;

  if (openCalibrationModal && !hasCalibrationSetting) {
    return (
      <Stack
        sx={{
          padding: 1,
          paddingLeft: 0,
          height: '100%',
        }}
        spacing={0}
        alignContent="flex-start"
      >
        <SecondaryBody metadata>
          {t('match_strength_explanation.set_calibration')}
        </SecondaryBody>
        <ButtonWrapper>
          <TertiaryButton onClick={handleClick} size="md" icon={AiMagicIcon}>
            {t('match_strength_explanation.calibrate')}
          </TertiaryButton>
        </ButtonWrapper>
      </Stack>
    );
  }

  if (!hasScores || parsingInProgress || parsingFailed) {
    const statusMessage = parsingInProgress
      ? t('resume_details.parsing_in_progress')
      : t('match_strength_explanation.needs_manual_review');

    return (
      <Stack
        sx={{
          padding: 1,
          paddingLeft: 0,
          height: '100%',
        }}
        alignContent="flex-start"
      >
        <SecondaryBody metadata>{statusMessage}</SecondaryBody>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      sx={{
        padding: 1,
        paddingLeft: 0,
        paddingTop: 1.5,
        height: '100%',
      }}
    >
      <StrengthIndicator matchStrength={matchStrength as MatchStrength} />
      <Stack direction="row" spacing={1} alignItems="center">
        <SecondaryBody>{t(`match_strengths.${matchStrength}`)}</SecondaryBody>
        {isOverride && <WarningTag text={t('override')} />}
      </Stack>
      <MatchingSkills
        skills={matchedSkills}
        openCalibrationModal={openCalibrationModal}
      />
    </Stack>
  );
};

export default BaseMatchStrengthCell;
